import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'motion/react';
import { ArrowRight, BookOpen, GraduationCap, Compass } from 'lucide-react';
import { getPublicBaseUrl } from '@/lib/publicBaseUrl';
import { useRegion } from '@/contexts/RegionContext';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { SocialShare } from '@/components/ui/SocialShare';

export interface EnneagramTypeData {
  typeNumber: number;
  typeName: string;
  styleName: string;
  title: string;
  description: string;
  path: string;
  corePattern: {
    avoids: (isUS: boolean) => string;
    strivesTo: (isUS: boolean) => string;
    focusesOn: (isUS: boolean) => string;
  };
  intro: (isUS: boolean) => string;
  giftsParagraphs: (isUS: boolean) => string[];
  stuckParagraphs: (isUS: boolean) => string[];
  fiveLensParagraphs: (isUS: boolean) => string[];
  inTeamsParagraphs: (isUS: boolean) => string[];
}

export const ENNEAGRAM_TYPES_DATA: EnneagramTypeData[] = [
  // TYPE 1
  {
    typeNumber: 1,
    typeName: "The Strict Perfectionist",
    styleName: "The Pursuit of Quality",
    title: "Enneagram Type 1: The Strict Perfectionist | FIVE LENS Enneagram",
    description: "Explore Enneagram Type 1 (The Strict Perfectionist) in the FIVE LENS framework. Understand core motivations, growth pathways, and leadership dynamics.",
    path: "/enneagram-type-1",
    corePattern: {
      avoids: (isUS) => `Criticism, error, and being perceived as ineffective or morally flawed in ${isUS ? 'behavior' : 'behaviour'}.`,
      strivesTo: (isUS) => `Deliver perfection, maintain personal integrity, and uphold the highest ${isUS ? 'organizational' : 'organisational'} standards.`,
      focusesOn: (isUS) => `Doing things the "right way," identifying flaws, and establishing clear logical order.`
    },
    intro: (isUS) => `Enneagram Type 1 individuals bring exceptional clarity, moral principle, and rigorous standards to leadership and ${isUS ? 'organizational' : 'organisational'} dynamics. Driven by a passion for accuracy and ethical execution, Type 1s act as guardians of systemic quality and continuous improvement. When supported by multi-lens self-awareness, their pursuit of excellence evolves from reactive fault-finding into transformative, inspiring leadership.`,
    giftsParagraphs: (isUS) => [
      `Type 1s provide the structural backbone for operational excellence, precision, and unyielding integrity across teams. They possess a natural ability to detect discrepancies and risk factors before they escalate into systemic failures, creating robust workflows that withstand rigorous audit.`,
      `Their dedication to fairness, accountability, and ethical governance inspires confidence among stakeholders. At their best, Type 1 leaders model exemplary personal ${isUS ? 'behavior' : 'behaviour'}, guiding ${isUS ? 'organizations' : 'organisations'} toward high-trust environments where quality is never compromised.`
    ],
    stuckParagraphs: (isUS) => [
      `Under pressure or tight deadlines, a Type 1's internal critic can become an external bottleneck. They may experience perfectionistic paralysis, hyper-criticism, and underlying resentment when colleagues fall short of unstated or ideal standards.`,
      `Over-checking details and rigid adherence to rules can delay execution and create anxiety within teams who feel micro-managed. In stressful periods, their ${isUS ? 'defense' : 'defence'} mechanism manifests as reaction formation, suppressing anger behind a controlled, critical exterior.`
    ],
    fiveLensParagraphs: (isUS) => [
      `Traditional Enneagram models frequently reduce Type 1 to a fixed "perfectionist" label, ignoring how stress, context, and maturity shape daily actions. FIVE LENS goes much further by measuring all nine ${isUS ? 'behavioral' : 'behavioural'} patterns across a leader's profile, rather than forcing individuals into a single category.`,
      `By integrating Personal Mastery (inspired by Peter Senge) for vertical growth, Emotional Resilience metrics to manage internal pressure, Social Drives (grounded in Maslow and Barrett) to analyze belonging versus status, and ${isUS ? 'Energy Centers' : 'Energy Centres'} (Head/Heart/Gut) for triadic alignment, our diagnostic provides a complete picture.`,
      `Through the Premium Personal Feedback Report (PFR) and Strategic Coaching Feedback, Type 1 leaders receive a ${isUS ? 'personalized' : 'personalised'} roadmap to shift from punitive perfectionism to wise, adaptable leadership.`
    ],
    inTeamsParagraphs: (isUS) => [
      `During Team Discovery Workshops and Real-Play Skills Workshops, Type 1s serve as essential catalysts for quality assurance and governance.`,
      `When teams move past "What type are you?" and focus on collective alignment, Type 1s learn to channel their critical eye into constructive coaching while learning to trust team capability, turning operational drag into sustained high performance.`
    ]
  },

  // TYPE 2
  {
    typeNumber: 2,
    typeName: "The Considerate Helper",
    styleName: "Service and Support",
    title: "Enneagram Type 2: The Considerate Helper | FIVE LENS Enneagram",
    description: "Explore Enneagram Type 2 (The Considerate Helper) in the FIVE LENS framework. Understand core motivations, relationship dynamics, and leadership growth.",
    path: "/enneagram-type-2",
    corePattern: {
      avoids: (isUS) => `Addressing their own needs directly or being perceived as unhelpful, selfish, or unappreciated.`,
      strivesTo: (isUS) => `Be helpful, supportive, generous, and indispensable to colleagues and leadership.`,
      focusesOn: (isUS) => `The emotional well-being, interpersonal needs, and subtle expectations of team members.`
    },
    intro: (isUS) => `Enneagram Type 2 leaders and professionals bring deep empathy, relational warmth, and a proactive spirit of service to ${isUS ? 'organizational' : 'organisational'} culture. They excel at building rapport, anticipating human needs, and nurturing talent across cross-functional groups. When developed through validated assessments, their generosity becomes a strategic asset for sustained collaboration and psychological safety.`,
    giftsParagraphs: (isUS) => [
      `Type 2s create the relational glue that holds ${isUS ? 'organizations' : 'organisations'} together during times of change. They build warm, high-trust environments where team members feel genuinely valued, supported, and ${isUS ? 'recognized' : 'recognised'}.`,
      `Their intuitive grasp of interpersonal dynamics enables them to resolve interpersonal friction early, foster inclusive workplace cultures, and act as empathetic Thinking Partners for colleagues under strain.`
    ],
    stuckParagraphs: (isUS) => [
      `Under heavy workload or emotional pressure, Type 2s may over-extend themselves, struggle with boundary setting, and neglect their own professional priorities.`,
      `If their supportive efforts go unacknowledged, hidden resentment or indirect communication can emerge. Their ${isUS ? 'defense' : 'defence'} mechanism of seduction or over-involvement can blur professional boundaries and lead to relational exhaustion.`
    ],
    fiveLensParagraphs: (isUS) => [
      `Where traditional personality tests box Type 2 into a simplistic "helper" persona, FIVE LENS delivers a multi-dimensional diagnostic that measures how all nine Enneagram patterns operate across different scenarios.`,
      `We evaluate Personal Mastery (inspired by Peter Senge) to cultivate self-leadership, Emotional Resilience to measure stress recovery, Social Drives (Maslow and Barrett) to examine status versus connection, and ${isUS ? 'Energy Centers' : 'Energy Centres'} (Head/Heart/Gut) to balance heart-centered empathy with gut-level boundaries.`,
      `The Premium Personal Feedback Report (PFR) provides Strategic Coaching Feedback that equips Type 2s to ${isUS ? 'practice' : 'practise'} self-care and transition from over-helping to empowering, strategic leadership.`
    ],
    inTeamsParagraphs: (isUS) => [
      `In Team Discovery Workshops, Type 2s play a pivotal role in creating genuine psychological safety and mutual encouragement.`,
      `By moving from static individual labels to dynamic team alignment, Type 2s learn to express their own needs clearly while continuing to elevate team morale and cross-functional synergy.`
    ]
  },

  // TYPE 3
  {
    typeNumber: 3,
    typeName: "The Competitive Achiever",
    styleName: "Achievement & Results",
    title: "Enneagram Type 3: The Competitive Achiever | FIVE LENS Enneagram",
    description: "Explore Enneagram Type 3 (The Competitive Achiever) in the FIVE LENS framework. Discover drive, performance focus, and authentic leadership maturity.",
    path: "/enneagram-type-3",
    corePattern: {
      avoids: (isUS) => `Failure, inefficiency, and appearing incompetent, slow, or unsuccessful.`,
      strivesTo: (isUS) => `Achieve ambitious goals, maximize efficiency, and be ${isUS ? 'recognized' : 'recognised'} as a top performer.`,
      focusesOn: (isUS) => `Key performance indicators, professional image, and finding streamlined execution paths.`
    },
    intro: (isUS) => `Enneagram Type 3 professionals drive momentum, high performance, and goal attainment with focused energy and strategic adaptability. Highly motivated by progress, efficiency, and measurable success, Type 3s excel at inspiring teams to hit key targets. Through holistic development, their drive for achievement expands into authentic leadership that balances task completion with human sustainability.`,
    giftsParagraphs: (isUS) => [
      `Type 3s are natural catalysts for execution and ${isUS ? 'organizational' : 'organisational'} velocity. They translate complex strategic visions into actionable, measurable milestones and energize others through their commitment.`,
      `Their pragmatic, results-driven approach cuts through bureaucratic drag. They possess a remarkable ability to adapt their communication style to engage diverse audiences and deliver high-impact presentation outcomes.`
    ],
    stuckParagraphs: (isUS) => [
      `When pressure mounts, Type 3s risk prioritizing speed and image over depth and team well-being. They may overwork, bypass critical emotional cues, or treat colleagues as instruments for target delivery.`,
      `In high-stress environments, their ${isUS ? 'defense' : 'defence'} mechanism of identification with work output can lead to burnout, superficial alignment, and a loss of personal authenticity.`
    ],
    fiveLensParagraphs: (isUS) => [
      `Unlike traditional models that reduce leaders to a fixed "achiever" type, FIVE LENS evaluates all nine ${isUS ? 'behavioral' : 'behavioural'} patterns simultaneously across different levels of pressure and vertical maturity.`,
      `By integrating Personal Mastery (Peter Senge) for vertical growth, Emotional Resilience metrics to track recovery from setbacks, Social Drives (grounded in Maslow and Barrett) to analyze status and recognition needs, and ${isUS ? 'Energy Centers' : 'Energy Centres'} (Head/Heart/Gut), our framework unlocks deeper potential.`,
      `The Premium Personal Feedback Report (PFR) offers Strategic Coaching Feedback that helps Type 3s ground their high performance in genuine personal integrity and long-term team sustainability.`
    ],
    inTeamsParagraphs: (isUS) => [
      `In Team Discovery Workshops, Type 3s bring high energy, goal orientation, and clear focus on key deliverables.`,
      `By shifting from "What type are you?" to analyzing overall team patterns, Type 3s learn to value process and emotional check-ins, ensuring that high achievement is both sustainable and shared.`
    ]
  },

  // TYPE 4
  {
    typeNumber: 4,
    typeName: "The Intense Individualist",
    styleName: "Creativity & Meaning",
    title: "Enneagram Type 4: The Intense Individualist | FIVE LENS Enneagram",
    description: "Explore Enneagram Type 4 (The Intense Individualist) in the FIVE LENS framework. Learn about emotional depth, creativity, and self-mastery.",
    path: "/enneagram-type-4",
    corePattern: {
      avoids: (isUS) => `The ordinary, superficial routines, and feeling misunderstood, plain, or unappreciated.`,
      strivesTo: (isUS) => `Express authentic identity, create unique value, and find deep personal meaning in work.`,
      focusesOn: (isUS) => `Emotional depth, qualitative nuances, personal significance, and aesthetic harmony.`
    },
    intro: (isUS) => `Enneagram Type 4 individuals contribute profound creativity, authenticity, and emotional depth to leadership and ${isUS ? 'organizational' : 'organisational'} design. Unafraid to explore complex human experiences and question conventional assumptions, Type 4s design unique solutions and bring rich qualitative perspective. With structured self-mastery, their sensitivity transforms into visionary innovation and empathetic leadership.`,
    giftsParagraphs: (isUS) => [
      `Type 4s bring originality, empathy, and creative courage to problem-solving. They spot subtle emotional shifts, champion human-centered values, and challenge cookie-cutter approaches that lack soul.`,
      `Their commitment to authenticity encourages teams to engage in honest, meaningful dialogue and produce work that truly resonates on a human level.`
    ],
    stuckParagraphs: (isUS) => [
      `Under stress, Type 4s can retreat into emotional intensity, feeling isolated or under-valued by the ${isUS ? 'organization' : 'organisation'}. They may struggle with mood fluctuations and over-identify with internal states.`,
      `Their ${isUS ? 'defense' : 'defence'} mechanism of introjection can lead them to reject standardized processes or feel misunderstood, creating friction with operational schedules and analytical frameworks.`
    ],
    fiveLensParagraphs: (isUS) => [
      `Traditional Enneagram typing often stereotypically labels Type 4 as "dramatic" or "impractical." FIVE LENS moves past fixed categories by measuring the presence of all nine styles across a continuum of maturity.`,
      `Combining Personal Mastery (inspired by Peter Senge) for vertical development, Emotional Resilience metrics, Social Drives (Maslow and Barrett), and Head/Heart/Gut ${isUS ? 'Energy Centers' : 'Energy Centres'}, our assessment provides unparalleled depth.`,
      `Through the Premium Personal Feedback Report (PFR) and Strategic Coaching Feedback, coaches give Type 4s precise leverage to ground their creative vision in practical execution and emotional resilience.`
    ],
    inTeamsParagraphs: (isUS) => [
      `During Team Discovery Workshops, Type 4s encourage teams to look beneath surface metrics and connect with core purpose.`,
      `As teams shift focus to shared ${isUS ? 'behavioral' : 'behavioural'} alignment, Type 4s discover how their unique creative voice aligns with operational goals, fostering both individual expression and collective strength.`
    ]
  },

  // TYPE 5
  {
    typeNumber: 5,
    typeName: "The Quiet Specialist",
    styleName: "Knowledge & Insight",
    title: "Enneagram Type 5: The Quiet Specialist | FIVE LENS Enneagram",
    description: "Explore Enneagram Type 5 (The Quiet Specialist) in the FIVE LENS framework. Understand analytical depth, boundaries, and strategic engagement.",
    path: "/enneagram-type-5",
    corePattern: {
      avoids: (isUS) => `Intellectual intrusion, feeling unprepared, and emotional or energy depletion.`,
      strivesTo: (isUS) => `Master complex domains, maintain intellectual autonomy, and conserve personal resources.`,
      focusesOn: (isUS) => `Objective data, logical systems, independent research, and clear personal boundaries.`
    },
    intro: (isUS) => `Enneagram Type 5 leaders and technical experts provide rigorous analytical thinking, objective research, and deep strategic foresight. Driven by a desire for understanding and mastery, Type 5s excel at unraveling intricate technical challenges and conceptualizing long-term models. When supported by holistic development, their intellectual clarity becomes a pillar for sound ${isUS ? 'organizational' : 'organisational'} decision-making.`,
    giftsParagraphs: (isUS) => [
      `Type 5s bring unmatched clarity, thorough investigation, and calm objectivity to high-stakes environments. They filter out emotional noise to evaluate facts coolly, synthesize complex information into elegant models, and safeguard systemic integrity.`,
      `Their ${isUS ? 'specialized' : 'specialised'} knowledge and independent perspective make them invaluable advisors when navigating intricate technological or market transformations.`
    ],
    stuckParagraphs: (isUS) => [
      `In high-demand settings, Type 5s may withdraw into intellectual isolation, withholding information or delaying action until "all data is ${isUS ? 'analyzed' : 'analysed'}."`,
      `Their detachment can be misread as coldness or disinterest. Their ${isUS ? 'defense' : 'defence'} mechanism of isolation of affect can create communication gaps with cross-functional team members who seek emotional connection.`
    ],
    fiveLensParagraphs: (isUS) => [
      `Rather than putting technical specialists into a narrow "investigator" box, FIVE LENS assesses the full spectrum of all nine Enneagram patterns across varying pressure levels.`,
      `By layering Personal Mastery (inspired by Peter Senge), Emotional Resilience, Social Drives (analyzing status versus autonomy), and ${isUS ? 'Energy Centers' : 'Energy Centres'} (activating Gut and Heart intelligence alongside Head knowledge), our model builds complete executive capability.`,
      `The Premium Personal Feedback Report (PFR) delivers Strategic Coaching Feedback that empowers Type 5s to step forward as engaging, impactful leaders.`
    ],
    inTeamsParagraphs: (isUS) => [
      `In Team Discovery Workshops, Type 5s serve as invaluable analytical anchors and objective soundboards.`,
      `By shifting from "What type are you?" to "How do we show up as a team?", Type 5s learn to share their insights earlier, build collaborative trust, and actively participate in real-time execution.`
    ]
  },

  // TYPE 6
  {
    typeNumber: 6,
    typeName: "The Loyal Skeptic",
    styleName: "Vigilance & Security",
    title: "Enneagram Type 6: The Loyal Skeptic | FIVE LENS Enneagram",
    description: "Explore Enneagram Type 6 (The Loyal Skeptic) in the FIVE LENS framework. Uncover courage, trust-building, and risk management dynamics.",
    path: "/enneagram-type-6",
    corePattern: {
      avoids: (isUS) => `Unpreparedness, hidden risks, betrayal, and chaotic or untrustworthy environments.`,
      strivesTo: (isUS) => `Build security, establish reliable structures, and foster loyal team alliances.`,
      focusesOn: (isUS) => `Risk mitigation, contingency planning, authority dynamics, and trust validation.`
    },
    intro: (isUS) => `Enneagram Type 6 leaders and managers bring strategic risk awareness, deep loyalty, and exceptional problem-solving diligence to ${isUS ? 'organizations' : 'organisations'}. Constantly assessing potential threats and building contingency plans, Type 6s ensure operational resilience and team stability. When elevated through multi-dimensional development, their vigilance transforms into courageous, decisive leadership and unshakeable trust.`,
    giftsParagraphs: (isUS) => [
      `Type 6s are the ultimate defenders of ${isUS ? 'organizational' : 'organisational'} security and team cohesion. Their foresight identifies vulnerabilities before disasters strike, while their deep loyalty creates dependable, close-knit cultures.`,
      `They excel at risk governance, practical troubleshooting, and standing by colleagues in challenging times, earning widespread trust through their steadfast commitment.`
    ],
    stuckParagraphs: (isUS) => [
      `Under prolonged uncertainty, Type 6s can succumb to anxious over-analysis, worst-case scenario thinking, or defensive skepticism.`,
      `Their cautiousness may stall innovation or manifest as resistance to change. Their ${isUS ? 'defense' : 'defence'} mechanism of projection can lead to mistrust of authority or misplaced suspicion during strategic shifts.`
    ],
    fiveLensParagraphs: (isUS) => [
      `Traditional Enneagram models frequently categorize Type 6 as merely "anxious" or "reactive." FIVE LENS offers a far richer diagnostic by measuring all nine patterns alongside vertical development.`,
      `Combining Personal Mastery (Peter Senge framework), Emotional Resilience tracking, Social Drives analysis (Maslow and Barrett), and ${isUS ? 'Energy Centers' : 'Energy Centres'} alignment, our approach builds true inner security.`,
      `The Premium Personal Feedback Report (PFR) delivers Strategic Coaching Feedback that builds internal confidence and reduces anticipatory anxiety in Type 6 leaders.`
    ],
    inTeamsParagraphs: (isUS) => [
      `In Team Discovery Workshops and Real-Play Skills Workshops, Type 6s help teams anticipate obstacles without becoming paralyzed.`,
      `Moving past "What type are you?", Type 6s leverage their risk-mapping capabilities to build resilient, high-trust team strategies that balance caution with bold innovation.`
    ]
  },

  // TYPE 7
  {
    typeNumber: 7,
    typeName: "The Enthusiastic Visionary",
    styleName: "Optimism & Vision",
    title: "Enneagram Type 7: The Enthusiastic Visionary | FIVE LENS Enneagram",
    description: "Explore Enneagram Type 7 (The Enthusiastic Visionary) in the FIVE LENS framework. Discover innovation, focus, and emotional resilience.",
    path: "/enneagram-type-7",
    corePattern: {
      avoids: (isUS) => `Constraint, emotional pain, boredom, and restrictive operational boundaries.`,
      strivesTo: (isUS) => `Maintain freedom, explore exciting possibilities, and keep options open.`,
      focusesOn: (isUS) => `Future innovations, strategic alternatives, creative connections, and positive momentum.`
    },
    intro: (isUS) => `Enneagram Type 7 professionals energize ${isUS ? 'organizations' : 'organisations'} with boundless innovation, strategic agility, and infectious optimism. Gifted at synthesizing disparate ideas and spotting future market opportunities, Type 7s inspire teams to think boldly and break through conventional limits. Through structured developmental grounding, their expansive vision evolves into sustained, high-impact execution.`,
    giftsParagraphs: (isUS) => [
      `Type 7s drive strategic growth, creative problem-solving, and ${isUS ? 'organizational' : 'organisational'} resilience. They reframe setbacks into compelling opportunities and connect disparate concepts into breakthrough innovations.`,
      `Their upbeat, agile approach fosters dynamic workplace environments where change is embraced as an exciting frontier rather than a threat.`
    ],
    stuckParagraphs: (isUS) => [
      `When faced with repetitive execution or uncomfortable emotional realities, Type 7s may scatter their focus across too many competing initiatives.`,
      `Their tendency to avoid difficult conversations or leap to the next big project can leave teams feeling disoriented. Their ${isUS ? 'defense' : 'defence'} mechanism of rationalization can mask real operational problems behind superficial optimism.`
    ],
    fiveLensParagraphs: (isUS) => [
      `Traditional Enneagram tools often reduce Type 7s to a superficial "enthusiast" label. FIVE LENS measures all nine Enneagram patterns simultaneously to capture the full breadth of a leader's capability.`,
      `By integrating Personal Mastery for vertical discipline, Emotional Resilience metrics, Social Drives, and ${isUS ? 'Energy Centers' : 'Energy Centres'} (connecting Head vision with Gut execution and Heart empathy), our model ensures deep integration.`,
      `The Premium Personal Feedback Report (PFR) provides Strategic Coaching Feedback that helps Type 7s turn rapid ideation into disciplined, long-term impact.`
    ],
    inTeamsParagraphs: (isUS) => [
      `During Team Discovery Workshops, Type 7s act as powerful ideation catalysts and energy boosters.`,
      `As the workshop shifts focus to collective team dynamics, Type 7s learn to anchor their enthusiasm in shared commitments, ensuring that inspiring ideas transition seamlessly into measurable operational success.`
    ]
  },

  // TYPE 8
  {
    typeNumber: 8,
    typeName: "The Active Controller",
    styleName: "Strength & Drive",
    title: "Enneagram Type 8: The Active Controller | FIVE LENS Enneagram",
    description: "Explore Enneagram Type 8 (The Active Controller) in the FIVE LENS framework. Learn about executive presence, power, and vulnerable strength.",
    path: "/enneagram-type-8",
    corePattern: {
      avoids: (isUS) => `Vulnerability, manipulation, weakness, and loss of control over their domain.`,
      strivesTo: (isUS) => `Be strong, decisive, direct, and protect their team and strategic vision.`,
      focusesOn: (isUS) => `Power dynamics, direct action, justice, resource control, and removing obstacles.`
    },
    intro: (isUS) => `Enneagram Type 8 leaders bring decisive power, executive presence, and a fierce commitment to justice and protection. Natural drivers of change, Type 8s confront obstacles head-on, champion the vulnerable, and take bold responsibility for ${isUS ? 'organizational' : 'organisational'} outcomes. When tempered by self-mastery and emotional intelligence, their commanding strength matures into empowering, high-trust leadership.`,
    giftsParagraphs: (isUS) => [
      `Type 8s provide unwavering courage, strategic clarity, and decisive momentum in times of crisis. They cut through ambiguity, protect their teams from external interference, and instill confidence through their willingness to take tough decisions.`,
      `Their candid, direct communication style establishes clear boundaries and removes organizational drag, enabling rapid movement toward major targets.`
    ],
    stuckParagraphs: (isUS) => [
      `Under intense pressure or pushback, Type 8s can become overly dominant, blunt, or intimidating. Their instinct to take charge can crush subtle input from quieter colleagues and create unnecessary conflict.`,
      `Their ${isUS ? 'defense' : 'defence'} mechanism of denial masks their own physical or emotional vulnerability, leading to over-exertion and sudden relationship friction.`
    ],
    fiveLensParagraphs: (isUS) => [
      `Traditional personality tests often box Type 8 into a harsh "controller" stereotype. FIVE LENS ${isUS ? 'recognizes' : 'recognises'} that true strength includes vulnerability and balance, measuring all nine patterns across the leader's profile.`,
      `By incorporating Peter Senge's Personal Mastery, Emotional Resilience indicators, Social Drives (belonging and status balance), and ${isUS ? 'Energy Centers' : 'Energy Centres'} integration (balancing Gut instinct with Heart compassion and Head analysis), our diagnostic goes deeper.`,
      `The Premium Personal Feedback Report (PFR) guides Type 8 leaders toward transformative, servant-hearted authority.`
    ],
    inTeamsParagraphs: (isUS) => [
      `In Team Discovery Workshops, Type 8s provide strong momentum and honest, candid feedback.`,
      `Shifting from "What type are you?" to shared ${isUS ? 'behavioral' : 'behavioural'} alignment, Type 8s learn to step back, listen actively, and empower team members, building a culture of mutual accountability and psychological safety.`
    ]
  },

  // TYPE 9
  {
    typeNumber: 9,
    typeName: "The Adaptive Peacemaker",
    styleName: "Harmony & Stability",
    title: "Enneagram Type 9: The Adaptive Peacemaker | FIVE LENS Enneagram",
    description: "Explore Enneagram Type 9 (The Adaptive Peacemaker) in the FIVE LENS framework. Discover consensus-building, conflict resolution, and decisive action.",
    path: "/enneagram-type-9",
    corePattern: {
      avoids: (isUS) => `Conflict, discord, pressure to confront, and feeling disconnected from others.`,
      strivesTo: (isUS) => `Maintain internal peace, build consensus, and create stable, harmonious environments.`,
      focusesOn: (isUS) => `Multiple viewpoints, common ground, maintaining calm, and avoiding friction.`
    },
    intro: (isUS) => `Enneagram Type 9 leaders and practitioners excel at unifying diverse perspectives, mediating complex conflicts, and creating harmonious, inclusive ${isUS ? 'organizations' : 'organisations'}. Possessing a natural capacity for empathetic listening and broad synthesis, Type 9s build consensus across silos and steady teams during turbulent times. With proactive self-leadership, their unifying presence becomes a steady driver of sustainable progress.`,
    giftsParagraphs: (isUS) => [
      `Type 9s are exceptional bridge-builders and consensus leaders. They synthesize conflicting demands into win-win solutions, ensure every voice is heard, and calm high-tension situations through their grounded, patient presence.`,
      `Their diplomatic skill reduces friction and creates long-term ${isUS ? 'organizational' : 'organisational'} alignment across cross-functional departments.`
    ],
    stuckParagraphs: (isUS) => [
      `In high-stakes or fast-moving scenarios, Type 9s may minimize urgent issues, delay tough decisions, or suppress their own positions to preserve superficial peace.`,
      `Their conflict avoidance can lead to passive resistance, operational drag, and unexpressed frustration. Their ${isUS ? 'defense' : 'defence'} mechanism of narcotization can result in merging with others' priorities while neglecting their own strategic agenda.`
    ],
    fiveLensParagraphs: (isUS) => [
      `Traditional Enneagram models frequently mischaracterize Type 9 as merely "passive" or "disengaged." FIVE LENS evaluates all nine patterns in parallel alongside vertical growth metrics.`,
      `By combining Personal Mastery for assertive self-leadership, Emotional Resilience measures, Social Drives analysis, and ${isUS ? 'Energy Centers' : 'Energy Centres'} balancing (awakening Gut-level drive and conviction), our assessment unlocks active leadership.`,
      `The Premium Personal Feedback Report (PFR) and Strategic Coaching Feedback equip Type 9s to lead with clear, decisive purpose.`
    ],
    inTeamsParagraphs: (isUS) => [
      `In Team Discovery Workshops and Real-Play Skills Workshops, Type 9s play a crucial role in synthesizing viewpoints and fostering unity.`,
      `Moving beyond static labels, Type 9s learn to assert their priorities openly and guide teams from passive harmony into proactive, high-performing alignment.`
    ]
  }
];

export interface BaseEnneagramTypePageProps {
  typeNumber: number;
  typeName: string;
  styleName: string;
  title: string;
  description: string;
  path: string;
  corePattern: {
    avoids: (isUS: boolean) => string;
    strivesTo: (isUS: boolean) => string;
    focusesOn: (isUS: boolean) => string;
  };
  intro: (isUS: boolean) => string;
  giftsParagraphs: (isUS: boolean) => string[];
  stuckParagraphs: (isUS: boolean) => string[];
  fiveLensParagraphs: (isUS: boolean) => string[];
  inTeamsParagraphs: (isUS: boolean) => string[];
}

export function BaseEnneagramTypePage(props: BaseEnneagramTypePageProps) {
  const { locale } = useRegion();
  const isUS = locale === 'en-US';
  const baseUrl = getPublicBaseUrl();
  const cleanSlug = props.path.replace(/^\/+/, '');
  const canonicalUrl = `${baseUrl}/${cleanSlug}`;

  return (
    <main className="pt-24 pb-24 min-h-screen bg-ground">
      <Helmet>
        <title>{props.title}</title>
        <meta name="description" content={props.description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={props.title} />
        <meta property="og:description" content={props.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:title" content={props.title} />
        <meta name="twitter:description" content={props.description} />
      </Helmet>

      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <Breadcrumb items={[
          { label: 'The Framework', href: '/#framework' },
          { label: 'The Enneagram', href: '/enneagram' },
          { label: `Type ${props.typeNumber}: ${props.typeName}` }
        ]} />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Eyebrow badge */}
          <div className="mb-6 flex items-center gap-3">
            <span className="h-[1px] w-8 bg-rule-3"></span>
            <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-rule-3">
              Lens 01 &middot; Enneagram Type {props.typeNumber}
            </span>
          </div>

          {/* Heading H1 */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight mb-4 text-ink font-normal">
            Enneagram Type {props.typeNumber}: {props.typeName}
          </h1>

          {/* Style Subtitle Badge */}
          <div className="mb-8">
            <span className="inline-block px-4 py-1.5 bg-ground-alt/60 border border-ground-deep text-xs font-bold text-rule-3 uppercase tracking-widest rounded-full">
              {props.styleName}
            </span>
          </div>

          {/* Intro Paragraph */}
          <p className="text-xl text-ink/80 font-light leading-relaxed mb-10">
            {props.intro(isUS)}
          </p>

          {/* Core Pattern Box */}
          <div className="bg-ground-alt/30 border border-ground-deep p-6 md:p-8 rounded-2xl mb-12">
            <h3 className="text-xs font-bold uppercase tracking-widest text-rule-3 mb-4">
              Core {isUS ? 'Behavioral' : 'Behavioural'} Pattern
            </h3>
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              <div className="bg-white/80 p-5 border border-ground-deep rounded-xl shadow-xs">
                <span className="block font-bold text-ink mb-1.5">Avoids</span>
                <span className="text-ink/80 font-light leading-relaxed">{props.corePattern.avoids(isUS)}</span>
              </div>
              <div className="bg-white/80 p-5 border border-ground-deep rounded-xl shadow-xs">
                <span className="block font-bold text-ink mb-1.5">Strives to</span>
                <span className="text-ink/80 font-light leading-relaxed">{props.corePattern.strivesTo(isUS)}</span>
              </div>
              <div className="bg-white/80 p-5 border border-ground-deep rounded-xl shadow-xs">
                <span className="block font-bold text-ink mb-1.5">Focuses on</span>
                <span className="text-ink/80 font-light leading-relaxed">{props.corePattern.focusesOn(isUS)}</span>
              </div>
            </div>
          </div>

          {/* Article Body Content */}
          <div className="prose prose-lg prose-brand max-w-none text-ink/80 leading-relaxed font-light">
            {/* 1. The Gift This Style Brings */}
            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-serif text-ink mb-4 font-normal">
                The Gift This Style Brings
              </h2>
              {props.giftsParagraphs(isUS).map((p, idx) => (
                <p key={idx} className="mb-6 text-ink/80 leading-relaxed font-light">
                  {p}
                </p>
              ))}
            </section>

            {/* 2. Where This Style Can Get Stuck */}
            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-serif text-ink mb-4 font-normal">
                Where This Style Can Get Stuck
              </h2>
              {props.stuckParagraphs(isUS).map((p, idx) => (
                <p key={idx} className="mb-6 text-ink/80 leading-relaxed font-light">
                  {p}
                </p>
              ))}
            </section>

            {/* 3. How FIVE LENS Goes Further */}
            <section className="bg-ground-alt/30 p-8 md:p-12 border border-ground-deep my-12 rounded-2xl">
              <h2 className="text-2xl font-serif text-ink mb-6 font-normal">
                How FIVE LENS Goes Further
              </h2>
              {props.fiveLensParagraphs(isUS).map((p, idx) => (
                <p key={idx} className="mb-4 last:mb-0 text-ink/80 leading-relaxed font-light">
                  {p}
                </p>
              ))}
            </section>

            {/* 4. In Teams */}
            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-serif text-ink mb-4 font-normal">
                In Teams
              </h2>
              {props.inTeamsParagraphs(isUS).map((p, idx) => (
                <p key={idx} className="mb-6 text-ink/80 leading-relaxed font-light">
                  {p}
                </p>
              ))}
            </section>

            {/* Blockquote with Hymie Gordon quote */}
            <blockquote className="border-l-4 border-rule-3 pl-6 py-2 my-12 bg-ground-alt/30 rounded-r-lg not-italic">
              <p className="text-xl font-serif italic text-ink/80 m-0">
                &ldquo;It gives clients a mirror, not a label. And that&rsquo;s where transformation begins.&rdquo;
              </p>
              <footer className="text-sm font-bold mt-4 tracking-widest uppercase text-rule-3">
                &mdash; Hymie Gordon
              </footer>
            </blockquote>
          </div>

          {/* Internal Links & Exploration Cards Grid */}
          <div className="space-y-8 mt-16 pt-8 border-t border-ground-deep">
            <h2 className="text-2xl md:text-3xl font-serif text-ink font-normal">
              Explore the FIVE LENS Methodology
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Internal Link 1: The Framework */}
              <Link 
                to="/framework" 
                className="group bg-white p-6 border border-ground-deep shadow-xs hover:border-rule-3 transition-all duration-200 flex flex-col justify-between rounded-xl"
              >
                <div>
                  <div className="w-10 h-10 bg-ground border border-ground-deep flex items-center justify-center text-ink group-hover:text-rule-3 transition-colors mb-4 rounded-lg">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <h3 className="font-serif text-lg font-medium text-ink group-hover:text-rule-3 transition-colors mb-2">
                    The Framework
                  </h3>
                  <p className="text-xs text-ink/70 font-light leading-relaxed mb-6">
                    Learn how the Enneagram works alongside Personal Mastery, Emotional Resilience, Social Drives, and {isUS ? 'Energy Centers' : 'Energy Centres'}.
                  </p>
                </div>
                <div className="flex items-center text-xs font-bold uppercase tracking-wider text-rule-3 gap-2">
                  View Framework <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Internal Link 2: Practitioner Certification */}
              <Link 
                to="/practitioner-certification" 
                className="group bg-white p-6 border border-ground-deep shadow-xs hover:border-rule-3 transition-all duration-200 flex flex-col justify-between rounded-xl"
              >
                <div>
                  <div className="w-10 h-10 bg-ground border border-ground-deep flex items-center justify-center text-ink group-hover:text-rule-3 transition-colors mb-4 rounded-lg">
                    <GraduationCap className="w-5 h-5" />
                  </div>
                  <h3 className="font-serif text-lg font-medium text-ink group-hover:text-rule-3 transition-colors mb-2">
                    Practitioner Certification
                  </h3>
                  <p className="text-xs text-ink/70 font-light leading-relaxed mb-6">
                    Get certified to administer full FIVE LENS diagnostics and debrief individual and team profiles with confidence.
                  </p>
                </div>
                <div className="flex items-center text-xs font-bold uppercase tracking-wider text-rule-3 gap-2">
                  {isUS ? 'Enroll in Certification' : 'Enrol in Certification'} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Internal Link 3: Team Diagnostic */}
              <Link 
                to="/diagnostic" 
                className="group bg-white p-6 border border-ground-deep shadow-xs hover:border-rule-3 transition-all duration-200 flex flex-col justify-between rounded-xl"
              >
                <div>
                  <div className="w-10 h-10 bg-ground border border-ground-deep flex items-center justify-center text-ink group-hover:text-rule-3 transition-colors mb-4 rounded-lg">
                    <Compass className="w-5 h-5" />
                  </div>
                  <h3 className="font-serif text-lg font-medium text-ink group-hover:text-rule-3 transition-colors mb-2">
                    Team Friction Diagnostic
                  </h3>
                  <p className="text-xs text-ink/70 font-light leading-relaxed mb-6">
                    Assess team friction patterns and identify dominant Enneagram dynamics across your leadership group.
                  </p>
                </div>
                <div className="flex items-center text-xs font-bold uppercase tracking-wider text-rule-3 gap-2">
                  Take Diagnostic <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
          </div>

          {/* Social Share */}
          <div className="mt-16 pt-8 border-t border-ground-deep">
            <SocialShare
              title={props.title}
              url={canonicalUrl}
            />
          </div>
        </motion.div>
      </div>
    </main>
  );
}

// Individual route components
export function EnneagramType1Page() {
  const data = ENNEAGRAM_TYPES_DATA[0];
  return <BaseEnneagramTypePage {...data} />;
}

export function EnneagramType2Page() {
  const data = ENNEAGRAM_TYPES_DATA[1];
  return <BaseEnneagramTypePage {...data} />;
}

export function EnneagramType3Page() {
  const data = ENNEAGRAM_TYPES_DATA[2];
  return <BaseEnneagramTypePage {...data} />;
}

export function EnneagramType4Page() {
  const data = ENNEAGRAM_TYPES_DATA[3];
  return <BaseEnneagramTypePage {...data} />;
}

export function EnneagramType5Page() {
  const data = ENNEAGRAM_TYPES_DATA[4];
  return <BaseEnneagramTypePage {...data} />;
}

export function EnneagramType6Page() {
  const data = ENNEAGRAM_TYPES_DATA[5];
  return <BaseEnneagramTypePage {...data} />;
}

export function EnneagramType7Page() {
  const data = ENNEAGRAM_TYPES_DATA[6];
  return <BaseEnneagramTypePage {...data} />;
}

export function EnneagramType8Page() {
  const data = ENNEAGRAM_TYPES_DATA[7];
  return <BaseEnneagramTypePage {...data} />;
}

export function EnneagramType9Page() {
  const data = ENNEAGRAM_TYPES_DATA[8];
  return <BaseEnneagramTypePage {...data} />;
}
